import { useScriptStore } from '@/shared/stores/script.store';

/**
 * Centralized Excel/CSV export utility.
 *
 * Usage mode 1 — Column-based (recommended, handles type coercion):
 * @param {Array} options.columns - Column definitions
 *   - header {string}: Column header text
 *   - key {string}: Property name to read from row (ignored if value is provided)
 *   - value {Function}: (row) => cellValue — custom extractor (takes precedence over key)
 *   - type {'string'|'number'}: Cell type. 'number' keeps numeric values so Excel treats them as numbers. Default: 'string'
 *   - numberFormat {string}: Optional Excel number format (e.g. '#,##0.00'). Only applies to xlsx, ignored for csv.
 * @param {Array} options.rows - Array of source data objects
 *
 * Usage mode 2 — Pre-built data (for complex layouts like section headers):
 * @param {Array} options.data - Pre-built array of {header: value} objects, used as-is
 * @param {Array} options.headers - Optional header strings array. Defaults to Object.keys of first row.
 *
 * Common options:
 * @param {string} options.fileName - File name without extension
 * @param {string} options.sheetName - Excel sheet tab name (default: 'Sheet1')
 * @param {string} options.format - 'xlsx' (default) or 'csv'
 * @param {Function} options.onEmpty - Called when data is empty; if not provided, export silently does nothing
 * @param {Function} options.onError - Called when export fails; receives the error object
 */
export function exportToFile({ columns, rows, data, headers, fileName, sheetName = 'Sheet1', format = 'xlsx', onEmpty = null, onError = null }) {
	const builtData = data || (columns && rows ? buildFromColumns(columns, rows) : null);
	if (!builtData || builtData.length === 0) {
		if (onEmpty) onEmpty();
		return;
	}

	const finalHeaders = headers || (columns ? columns.map(c => c.header) : Object.keys(builtData[0]));

	const scriptStore = useScriptStore();
	scriptStore.loadScript('xlsx', () => {
		try {
			const XLSX = window.XLSX;

			const worksheet = XLSX.utils.json_to_sheet(builtData);
			XLSX.utils.sheet_add_aoa(worksheet, [finalHeaders], { origin: 'A1' });

			if (columns) {
				applyNumberFormats(worksheet, columns, builtData.length);
			}
			autoFitColumns(worksheet, finalHeaders, builtData);

			const workbook = XLSX.utils.book_new();
			XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

			if (format === 'csv') {
				XLSX.writeFile(workbook, fileName + '.csv');
			} else {
				XLSX.writeFileXLSX(workbook, fileName + '.xlsx');
			}
		} catch (e) {
			console.error('Excel export failed:', e);
			if (onError) onError(e);
		}
	});
}

function buildFromColumns(columns, rows) {
	return rows.map(row => {
		const obj = {};
		columns.forEach(col => {
			let val;
			if (typeof col.value === 'function') {
				val = col.value(row);
			} else if (col.key) {
				val = row[col.key];
			}

			if (col.type === 'number') {
				obj[col.header] = (val !== null && val !== undefined && !isNaN(val)) ? Number(val) : 0;
			} else {
				obj[col.header] = val ?? '';
			}
		});
		return obj;
	});
}

function applyNumberFormats(worksheet, columns, rowCount) {
	const XLSX = window.XLSX;
	columns.forEach((col, colIdx) => {
		if (!col.numberFormat) return;
		for (let rowIdx = 0; rowIdx < rowCount; rowIdx++) {
			const cellRef = XLSX.utils.encode_cell({ r: rowIdx + 1, c: colIdx });
			if (worksheet[cellRef]) {
				worksheet[cellRef].z = col.numberFormat;
			}
		}
	});
}

function autoFitColumns(worksheet, headers, data) {
	worksheet['!cols'] = headers.map((header, i) => {
		let maxLen = header.length;
		data.forEach(row => {
			const val = row[header];
			const len = val !== null && val !== undefined ? String(val).length : 0;
			if (len > maxLen) maxLen = len;
		});
		return { wch: Math.min(maxLen + 2, 60) };
	});
}
